import React, { useCallback, useEffect, useState } from "react"
import { useDropzone } from "react-dropzone"
import { Box, Stack, InputLabel, Typography, Avatar, Modal, Slider, Button, Switch } from "@mui/material"
import { UploadFile, Delete } from "@mui/icons-material"
import Cropper, { Area, Point } from "react-easy-crop"
import { readFile } from "../../../Helpers/ImageHelper"
import getCroppedImg from "../ImageInput/CropImage"

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 800,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
} as const

export type ImageDropzoneProps = {
  title: string
  imageUrl?: string | null
  callbackFile: (file: string | null) => void
}

export default function ImageDropzone(props: ImageDropzoneProps) {
  const { title, imageUrl = null, callbackFile } = props

  const [open, setOpen] = useState(false)
  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)

  const [isApplyMonochromatic, setIsApplyMonochromatic] = useState(false)
  const [imageSrc, setImageSrc] = useState<string | null>(null)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [file, setFile] = useState<string | null>(null)

  useEffect(() => {
    if (imageUrl !== null) {
      setFile(imageUrl)
    }
  }, [imageUrl])

  const onFileChange = useCallback(async (nextFile: File) => {
    const imageDataUrl = (await readFile(nextFile)) as string
    setImageSrc(imageDataUrl)
    handleOpen()
  }, [])

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return
      onFileChange(acceptedFiles[0])
    },
    [onFileChange],
  )

  const showCroppedImage = async () => {
    if (!imageSrc || !croppedAreaPixels) return

    try {
      const croppedImage = (await getCroppedImg(imageSrc, croppedAreaPixels, isApplyMonochromatic)) as Blob
      const imageDataUrl = (await readFile(croppedImage as any)) as string
      setFile(imageDataUrl)
      callbackFile(imageDataUrl)
    } catch (e) {
      console.error(e)
    }
  }

  const onCropComplete = (_croppedArea: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels)
  }

  const { getRootProps, getInputProps } = useDropzone({ onDrop })

  const inputView = (
    <>
      <Box
        {...getRootProps()}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: 2,
          border: "1px solid",
          color: "grey.500",
          borderRadius: "6px",
          "&:hover": {
            color: "primary.main",
          },
        }}
      >
        <input {...getInputProps()} />
        <>
          <UploadFile />
          <Typography variant="body1" marginLeft={2}>
            {"Click to choose a file or drag here"}
          </Typography>
        </>
      </Box>

      <Modal open={open}>
        <Box sx={style}>
          <Typography variant="body1">Zoom</Typography>

          <Slider
            value={zoom}
            min={1}
            max={3}
            step={0.1}
            onChange={(_e, nextZoom) => setZoom(nextZoom as number)}
          />

          <Box
            sx={{
              marginTop: 2,
              position: "relative",
              width: "600px",
              height: "300px",
            }}
          >
            <Cropper
              image={imageSrc ?? undefined}
              crop={crop}
              zoom={zoom}
              aspect={2 / 1}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
            />
          </Box>

          <Box
            sx={{
              marginTop: 2,
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography variant="body1">Apply Monochromatic</Typography>
            <Switch checked={isApplyMonochromatic} onChange={(event) => setIsApplyMonochromatic(event.target.checked)} />
          </Box>

          <Box
            sx={{
              marginTop: 2,
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Button color="error" onClick={() => handleClose()}>
              Close
            </Button>
            <Button onClick={showCroppedImage}>Done</Button>
          </Box>
        </Box>
      </Modal>
    </>
  )

  return (
    <Stack spacing={1} marginTop={4}>
      <InputLabel>{title}</InputLabel>
      {file === null ? inputView : <SelectedView src={file} deleteCallback={() => setFile(null)} />}
    </Stack>
  )
}

function SelectedView({ src, deleteCallback }: { src: string; deleteCallback: () => void }) {
  const [innerSrc] = useState(src)

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: 2,
        border: "1px dashed",
        color: "grey.500",
        borderRadius: "6px",
        "&:hover": {
          color: "primary.main",
        },
      }}
    >
      <Avatar variant="square" src={innerSrc} sx={{ width: 120, height: 60 }} />
      <Delete onClick={() => deleteCallback()} />
    </Box>
  )
}

